import React, { useContext, useState } from 'react';
import { useQuery } from 'react-query';

import { Form } from 'antd';
import styled from 'styled-components';

import { createCommunityLevelFees, getCommunityLevelFees, updateCommunityLevelFees } from 'apis/CommunityAPI';
import CommunityInfoConfirmationModal from 'components/CommunityInfo/CommunityInfoConfirmationModal';
import InputNumber from 'components/lib/InputNumber';

import { useRootContext } from '../../../layout/RootContext';
import { useEditDataModalContext } from '../../common/edit/EditDataModalContext';
import { CommunityInfoContext } from '../../CommunityInfoContext';
import { RateTrendsType } from '../types';

const Container = styled.div`
  display: flex;
  width: 800px;
  height: 150px;
  flex-direction: column;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 31px;
  margin-top: 5px;
  margin-bottom: 61px;

  .ant-form-item-additional {
    position: absolute;
  }
`;

const RowLabel = styled.div`
  font-family: var(--font-bold);
  font-size: 14px;
  color: var(--text-primary);
  min-width: 165px;
`;

type Props = {
  community?: any;
  disabled?: boolean;
};

const RateTrendsEdit = ({ community, disabled = false }: Props) => {
  const { showMessage } = useRootContext();
  const { communityInfo } = useContext(CommunityInfoContext);
  const { onSuccess, modified, setModified, setLoading, form } = useEditDataModalContext();

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingValues, setPendingValues] = useState<RateTrendsType | null>(null);

  const { data, isLoading, isFetched, refetch } = useQuery({
    queryKey: ['community', 'rate', 'trends', communityInfo?.id, community?.id],
    queryFn: () => getCommunityLevelFees(communityInfo?.id || community?.id, { active: 1 }),
    select: (data: RateTrendsType[]) => (data.length > 0 ? data[0] : undefined),
    enabled: Boolean(communityInfo || community),
  });

  // Show confirmation modal on submit attempt
  const handleSubmit = async (values: RateTrendsType) => {
    setPendingValues(values);
    setShowConfirmation(true);
  };

  // Actual submit after confirmation
  const handleConfirmSubmit = async () => {
    if (!pendingValues) return;

    try {
      setLoading(true);
      const updateFunction = pendingValues?.id ? updateCommunityLevelFees : createCommunityLevelFees;
      const payload = {
        ...pendingValues,
        community_id: communityInfo?.id || community?.id,
      };

      await updateFunction(communityInfo?.id ?? community?.id, payload).then((response) => {
        showMessage('success', 'Community Rate Trends added successfully');
        onSuccess();
        refetch();
      });
    } catch (error) {
      showMessage('error', 'Trouble updating community Rate Trends!');
    } finally {
      setLoading(false);
      setShowConfirmation(false);
      setPendingValues(null);
    }
  };

  const handleValuesChanged = () => {
    if (!modified) {
      setModified(true);
    }
  };

  return (
    <Container>
      {isFetched && !isLoading && (
        <>
          <Form
            form={form}
            disabled={disabled}
            initialValues={{ ...data }}
            onValuesChange={handleValuesChanged}
            onFinish={handleSubmit}
          >
            <Row>
              <RowLabel>Annual Rate Increase</RowLabel>
              <Form.Item noStyle name={'id'} hidden />
              <Form.Item noStyle name={'community'} hidden />
              <Form.Item
                name={'annual_rate_increase_min'}
                dependencies={['annual_rate_increase_max']}
                rules={[
                  {
                    type: 'number',
                    min: 0,
                    max: 100,
                    transform: (value) => (value ? Number(value) : undefined),
                    message: 'Minimal rate must be between 0 and 100',
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const maxRate = getFieldValue('annual_rate_increase_max');
                      const minIsSet = value != null && value !== null && value !== '';
                      const maxIsSet = maxRate != null && maxRate !== null && maxRate !== '';

                      if (!minIsSet && maxIsSet) {
                        return Promise.reject(new Error('Min rate is required if max is set'));
                      }

                      if (minIsSet && maxIsSet && parseFloat(value) >= parseFloat(maxRate)) {
                        return Promise.reject(new Error('Min rate must be less than max rate'));
                      }

                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <InputNumber style={{ height: 36, width: '160px' }} suffix="%" />
              </Form.Item>
              to
              <Form.Item
                name={'annual_rate_increase_max'}
                dependencies={['annual_rate_increase_min']}
                rules={[
                  {
                    type: 'number',
                    min: 0,
                    max: 100,
                    transform: (value) => (value ? Number(value) : undefined),
                    message: 'Maximal rate must be between 0 and 100',
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const minRate = getFieldValue('annual_rate_increase_min');

                      const maxIsSet = value != null && value !== null && value !== '';
                      const minIsSet = minRate != null && minRate !== null && minRate !== '';

                      if (!maxIsSet && minIsSet) {
                        return Promise.reject(new Error('Max rate is required if min is set'));
                      }

                      if (maxIsSet && minIsSet && parseFloat(value) <= parseFloat(minRate)) {
                        return Promise.reject(new Error('Max rate must be greater than min rate'));
                      }

                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <InputNumber style={{ height: 36, width: '160px' }} suffix="%" />
              </Form.Item>
            </Row>
          </Form>

          <CommunityInfoConfirmationModal
            showCommunityIncentivesConfirmationModal={showConfirmation}
            communityName={communityInfo?.comunity_name}
            onConfirm={handleConfirmSubmit}
            onClose={() => setShowConfirmation(false)}
          />
        </>
      )}
    </Container>
  );
};

export default RateTrendsEdit;
